import { useState } from 'react';
import { X, Loader2, Plus, AlertTriangle, MapPin } from 'lucide-react';
import { API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import { useActiveEvent } from '../../contexts/EventContext';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import MapLocationPicker from '../map/MapLocationPicker';
import type { MajorIncident, IncidentSeverity } from '../../types';

interface DeclareMajorIncidentModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (majorIncident: MajorIncident) => void;
}

export default function DeclareMajorIncidentModal({ open, onClose, onCreated }: DeclareMajorIncidentModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const { enabledIncidentSeverities } = useSystemConfig();
  const severityOptions = enabledIncidentSeverities.map((s) => s.id as IncidentSeverity);

  const [declaring, setDeclaring] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('critical');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [showMapPicker, setShowMapPicker] = useState(false);

  // METHAN details
  const [methan, setMethan] = useState({
    message: '',
    exactLocation: '',
    time: '',
    access: '',
    numbers: '',
    hazards: ''
  });

  // Commanders
  const [commanders, setCommanders] = useState<Array<{ name: string; role: string; department: string }>>([
    { name: '', role: 'Gold Commander', department: '' },
  ]);
  const [newCommanderName, setNewCommanderName] = useState('');
  const [newCommanderRole, setNewCommanderRole] = useState('Silver Commander');
  const [newCommanderDept, setNewCommanderDept] = useState('');

  const reset = () => {
    setTitle('');
    setDescription('');
    setSeverity('critical');
    setAddress('');
    setLatitude(0);
    setLongitude(0);
    setMethan({
      message: '',
      exactLocation: '',
      time: '',
      access: '',
      numbers: '',
      hazards: ''
    });
    setCommanders([{ name: '', role: 'Gold Commander', department: '' }]);
    setNewCommanderName('');
    setNewCommanderRole('Silver Commander');
    setNewCommanderDept('');
  };

  const handleAddCommander = () => {
    if (!newCommanderName.trim() || !newCommanderDept.trim()) return;
    setCommanders([...commanders, { name: newCommanderName, role: newCommanderRole, department: newCommanderDept }]);
    setNewCommanderName('');
    setNewCommanderRole('Silver Commander');
    setNewCommanderDept('');
  };

  const handleRemoveCommander = (idx: number) => {
    setCommanders(commanders.filter((_, i) => i !== idx));
  };

  const handleDeclare = async () => {
    if (!title.trim() || !description.trim() || !address.trim() || 
        !methan.message.trim() || !methan.exactLocation.trim() || !methan.time.trim() || 
        !methan.access.trim() || !methan.numbers.trim() || !methan.hazards.trim() ||
        commanders.some((c) => !c.name.trim())) return;
    setDeclaring(true);
    try {
      const res = await fetch(`${API_BASE_URL}/major-incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventId: activeEventId || '',
          title,
          description,
          severity,
          location: { latitude, longitude, address },
          commanders: commanders.map((c) => ({ name: c.name, role: c.role, department: c.department })),
          methan,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        onCreated?.(data.majorIncident);
        reset();
        onClose();
      }
    } catch { /* ignore */ } finally {
      setDeclaring(false);
    }
  };

  const handleMapSelect = (lat: number, lng: number, addr: string) => {
    setLatitude(lat);
    setLongitude(lng);
    setAddress(addr);
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="w-full max-w-2xl bg-card border border-border rounded-xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Declare Major Incident
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          <p className="text-xs text-muted-foreground mb-4">
            Following JESIP principles: Declare a major incident with commanding officers, location, and initial assessment.
          </p>

          <form onSubmit={(e) => { e.preventDefault(); handleDeclare(); }} className="space-y-4">
            {/* Title & Severity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Incident Title *</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="e.g. Major Transport Accident"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Severity</label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {severityOptions.map((s) => (
                    <option key={s} value={s}>{s.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                placeholder="Describe the major incident, initial assessment, and scope..."
                required
              />
            </div>

            {/* Location */}
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Location / Address *</label>
              <div className="flex gap-2">
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Address or coordinates"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowMapPicker(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 bg-primary/5 text-primary text-xs font-semibold hover:bg-primary/10 transition-colors flex-shrink-0"
                  title="Select location on map"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  Map
                </button>
              </div>
              {latitude !== 0 && longitude !== 0 && (
                <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                  Coords: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </p>
              )}
            </div>

            {/* METHAN Details */}
            <div className="border-t border-border pt-4">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                METHAN Report
                <span className="text-[9px] text-muted-foreground font-normal ml-2">(Major Incident Initial Report)</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Message *</label>
                  <input
                    value={methan.message}
                    onChange={(e) => setMethan(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Brief incident summary"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Exact Location *</label>
                  <input
                    value={methan.exactLocation}
                    onChange={(e) => setMethan(prev => ({ ...prev, exactLocation: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Specific location details"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Time *</label>
                  <input
                    value={methan.time}
                    onChange={(e) => setMethan(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Time of incident"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Access *</label>
                  <input
                    value={methan.access}
                    onChange={(e) => setMethan(prev => ({ ...prev, access: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Access routes, restrictions"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Numbers *</label>
                  <input
                    value={methan.numbers}
                    onChange={(e) => setMethan(prev => ({ ...prev, numbers: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Casualties, people involved"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">Hazards *</label>
                  <input
                    value={methan.hazards}
                    onChange={(e) => setMethan(prev => ({ ...prev, hazards: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Known hazards, risks"
                  />
                </div>
              </div>
            </div>

            {/* Commanders */}
            <div className="border-t border-border pt-4">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Commanding Officers</h3>
              <div className="space-y-2 mb-3">
                {commanders.map((cmd, idx) => (
                  <div key={idx} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <input
                        value={cmd.name}
                        onChange={(e) => {
                          const updated = [...commanders];
                          updated[idx].name = e.target.value;
                          setCommanders(updated);
                        }}
                        className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                        placeholder="Commander name"
                      />
                    </div>
                    <div className="flex-1">
                      <select
                        value={cmd.role}
                        onChange={(e) => {
                          const updated = [...commanders];
                          updated[idx].role = e.target.value;
                          setCommanders(updated);
                        }}
                        className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                      >
                        <option value="Gold Commander">Gold Commander</option>
                        <option value="Silver Commander">Silver Commander</option>
                        <option value="Bronze Commander">Bronze Commander</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <input
                        value={cmd.department}
                        onChange={(e) => {
                          const updated = [...commanders];
                          updated[idx].department = e.target.value;
                          setCommanders(updated);
                        }}
                        className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                        placeholder="Department"
                      />
                    </div>
                    {commanders.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveCommander(idx)}
                        className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors"
                      >
                        <X className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Commander */}
              <div className="flex gap-2 mb-3">
                <input
                  value={newCommanderName}
                  onChange={(e) => setNewCommanderName(e.target.value)}
                  className="flex-1 px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  placeholder="New commander name"
                />
                <select
                  value={newCommanderRole}
                  onChange={(e) => setNewCommanderRole(e.target.value)}
                  className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                >
                  <option value="Gold Commander">Gold</option>
                  <option value="Silver Commander">Silver</option>
                  <option value="Bronze Commander">Bronze</option>
                </select>
                <input
                  value={newCommanderDept}
                  onChange={(e) => setNewCommanderDept(e.target.value)}
                  className="flex-1 px-2 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                  placeholder="Department"
                />
                <button
                  type="button"
                  onClick={handleAddCommander}
                  className="p-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={declaring || !title.trim() || !description.trim() || !address.trim() || commanders.some((c) => !c.name.trim())}
                className="flex-1 py-2 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {declaring && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {declaring ? 'Declaring...' : 'Declare Major Incident'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showMapPicker && (
        <MapLocationPicker
          open={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          onSelect={handleMapSelect}
        />
      )}
    </>
  );
}
